/**
 * TOTP enrolment and step-up, against Supabase's native MFA.
 *
 * ## What was missing, and why it mattered
 *
 * `routes/auth/Mfa.tsx` has had the two screens since early on, and `SecurityPolicy.tsx` recorded
 * the gap precisely: they were "imported by nothing else — in no route table, and there is no
 * enrolment call in the repository". So `aal2` was UNREACHABLE in the browser. Nothing could enrol
 * a factor, and even a factor enrolled out of band was useless: sign-in never challenged, so the
 * session stayed at `aal1` for ever. Every enforcement path that reads assurance — the org's
 * `require_mfa` policy in `packages/core/src/auth/session.ts`, and now the staff console's gate
 * (ADR 0177) — was gating on a level no one could attain.
 *
 * This module is the missing half. It is deliberately the ONLY place that names
 * `client.auth.mfa`, so the screens hold no auth logic and a change to the vocabulary has one site.
 *
 * ## Assurance is Supabase's answer, never ours
 *
 * `currentLevel` and `nextLevel` come from `getAuthenticatorAssuranceLevel()`, which reads the
 * session's own claim. This module never infers "they have a factor, so they must be aal2": a
 * verified factor and a session that has actually presented it are different facts, and conflating
 * them is how a step-up gets skipped. The API agrees independently — `packages/core` derives
 * assurance from the token's native `aal`, so a browser that lied here would change nothing a
 * server believes.
 *
 * ## There are no recovery codes, and this module does not pretend otherwise
 *
 * Supabase issues none, and nothing in this repository redeems one. The enrolment screen used to
 * promise them; showing a list that no sign-in would ever accept is worse than showing none,
 * because somebody stores it and relies on it on the day they are locked out. The honest recovery
 * path is an operator removing the factor, which is what the screens now say.
 */
import { getClient } from '../supabase'

/** What the enrolment screen needs to show, and the id needed to confirm it. */
export type TotpEnrolment = {
  readonly factorId: string
  /** Base32, for typing in by hand when a camera is not an option. */
  readonly secret: string
  readonly uri: string
  /**
   * Supabase's own SVG, as a data URI. Rendered directly and never sent to a QR service: a live
   * TOTP secret posted to somebody else's server is the secret, given away.
   */
  readonly qrCode: string | null
}

export type AssuranceState = {
  readonly current: 'aal1' | 'aal2' | null
  readonly next: 'aal1' | 'aal2' | null
  /** True when the session must present a factor it has not yet presented. */
  readonly stepUpRequired: boolean
  /** The verified factor to challenge, when one is needed. */
  readonly factorId: string | null
}

export class MfaError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'MfaError'
  }
}

const NOT_CONFIGURED = new MfaError(
  'not_configured',
  'This deployment has no Supabase configuration, so two-factor authentication cannot be set up.',
)

function clientOrThrow() {
  const client = getClient()
  if (client === null) throw NOT_CONFIGURED
  return client
}

type Factor = { readonly id: string; readonly status: string; readonly friendly_name?: string }

/** Every TOTP factor on this account, verified or not. */
export async function listTotpFactors(): Promise<readonly Factor[]> {
  const { data, error } = await clientOrThrow().auth.mfa.listFactors()
  if (error) throw new MfaError('list_failed', error.message)
  return (data?.totp ?? []) as readonly Factor[]
}

export async function hasVerifiedFactor(): Promise<boolean> {
  return (await listTotpFactors()).some((f) => f.status === 'verified')
}

/**
 * Where this session stands, and what it would take to reach `aal2`.
 *
 * `stepUpRequired` is `nextLevel === 'aal2' && currentLevel !== 'aal2'` — Supabase's own comparison.
 * `nextLevel` is `aal2` only when the account holds a verified factor, so an account without one is
 * correctly not asked to step up rather than being locked out of its own sign-in.
 */
export async function assurance(): Promise<AssuranceState> {
  const client = clientOrThrow()
  const { data, error } = await client.auth.mfa.getAuthenticatorAssuranceLevel()
  if (error) throw new MfaError('assurance_failed', error.message)

  const current = (data?.currentLevel ?? null) as AssuranceState['current']
  const next = (data?.nextLevel ?? null) as AssuranceState['next']
  const stepUpRequired = next === 'aal2' && current !== 'aal2'

  let factorId: string | null = null
  if (stepUpRequired) {
    const verified = (await listTotpFactors()).find((f) => f.status === 'verified')
    factorId = verified?.id ?? null
  }
  return { current, next, stepUpRequired, factorId }
}

/**
 * Begin enrolment.
 *
 * Unverified factors are cleared first, and that is not tidiness. Supabase keeps a factor the
 * moment `enroll` is called, before any code is confirmed, so a person who opens the screen and
 * walks away leaves one behind — and the next attempt fails on the duplicate friendly name. The
 * failure reads as "two-factor is broken" and the cause is invisible. Only UNVERIFIED factors are
 * removed: a verified one is the person's working second factor and is never touched here.
 */
export async function enrolTotp(friendlyName = 'Authenticator app'): Promise<TotpEnrolment> {
  const client = clientOrThrow()

  for (const stale of await listTotpFactors()) {
    if (stale.status !== 'verified') {
      await client.auth.mfa.unenroll({ factorId: stale.id }).catch(() => undefined)
    }
  }

  const { data, error } = await client.auth.mfa.enroll({ factorType: 'totp', friendlyName })
  if (error) throw new MfaError('enrol_failed', error.message)
  if (!data) throw new MfaError('enrol_failed', 'the authenticator could not be set up')

  const totp = data.totp as { secret?: string; uri?: string; qr_code?: string } | undefined
  return {
    factorId: data.id,
    secret: totp?.secret ?? '',
    uri: totp?.uri ?? '',
    qrCode: totp?.qr_code ?? null,
  }
}

/**
 * Confirm enrolment with a code from the app.
 *
 * `challengeAndVerify` rather than a separate challenge and verify: the two are one action from the
 * person's side, and splitting them here would leave an unconsumed challenge behind on every
 * mistyped code. Succeeding also raises THIS session to `aal2`, which is why enrolling is enough to
 * reach a surface that demands it — no second sign-in.
 */
export async function confirmEnrolment(factorId: string, code: string): Promise<void> {
  const { error } = await clientOrThrow().auth.mfa.challengeAndVerify({ factorId, code })
  if (error) throw new MfaError('verify_failed', error.message)
}

/** The sign-in step-up. Same call; named separately because the two moments are not the same. */
export async function completeStepUp(factorId: string, code: string): Promise<void> {
  const { error } = await clientOrThrow().auth.mfa.challengeAndVerify({ factorId, code })
  if (error) throw new MfaError('verify_failed', error.message)
}

/**
 * Remove a factor.
 *
 * Supabase requires an `aal2` session to unenrol a verified factor, which is the correct shape: a
 * stolen `aal1` session must not be able to strip the second factor off the account it stole.
 */
export async function removeFactor(factorId: string): Promise<void> {
  const { error } = await clientOrThrow().auth.mfa.unenroll({ factorId })
  if (error) throw new MfaError('unenrol_failed', error.message)
}
